import { useCallback } from 'react'
import { FileText } from 'lucide-react'
import {
    createColumns,
    createDateFormatter,
    useAttachmentExpandable,
} from '@/components/ui/data-table-customized'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { useTranslation } from 'react-i18next'
import { Chip } from 'vnetwork-ui'
import { Color } from 'vnetwork-ui/dist/types'
import { CONTRACT_PROCESS_STATUS } from '../../constants'
import { ContractProcess } from '../../types'
import DownLoadIcons from '@/icons/DownLoadIcons'
import EyeIcons from '@/icons/EyeIcons'

/** Hook returning columns, expandable config, and file preview helpers for the process table */
export const useProcessColumns = () => {
    const { t } = useTranslation()
    const { openFilePreview, downloadFileWithPresigned } =
        useFilePreviewWithPresigned()

    const getCompletedAttachments = useCallback(
        (row: ContractProcess) =>
            row.contract_process_status === 'completed'
                ? row.attachments?.filter((a: any) => !a?.attachment_closed) ||
                  []
                : [],
        []
    )

    const attachmentExpandable = useAttachmentExpandable<ContractProcess>({
        getAttachments: getCompletedAttachments,
    })

    const columns: DataTableColumn<ContractProcess>[] = createColumns([
        {
            key: 'contract_process_id',
            title: '#',
            width: 60,
            cellClassName: 'text-primary',
        },
        {
            key: 'contract_process_title',
            title: t('contract_process_title'),
            width: 250,
            cellClassName: 'truncate',
        },
        {
            key: 'contract_process_status',
            title: t('status'),
            width: 120,
            render: (value) => {
                const status = CONTRACT_PROCESS_STATUS(t).find(
                    (s) => s.value === value
                )
                return status ? (
                    <Chip
                        label={status.label || (value as string)}
                        color={status.color as Color}
                        size="small"
                    />
                ) : (
                    (value as string) || '-'
                )
            },
        },
        {
            key: 'employee_name',
            title: t('creator'),
            width: 150,
        },
        {
            key: 'employee_names',
            title: t('assignees'),
            width: 200,
            render: (value) => {
                const names = value as string[]
                if (!names?.length)
                    return (
                        <span className="text-xs text-gray-400">
                            {t('no-assignees-for-process', { ns: 'contract' })}
                        </span>
                    )
                return (
                    <span className="text-xs truncate" title={names.join(', ')}>
                        {names.join(', ')}
                    </span>
                )
            },
        },
        {
            key: 'contract_process_deadline',
            title: t('contract_process_deadline'),
            width: 120,
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
        },
        {
            key: 'created_at',
            title: t('created_at'),
            width: 120,
            formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
        },
        {
            key: 'attachments',
            title: t('attachments'),
            width: 200,
            render: (_, row) => {
                const attachments = (
                    row as ContractProcess
                ).attachments?.filter((a: any) => !a?.attachment_closed)
                if (
                    !attachments?.length ||
                    (row as ContractProcess).contract_process_status !==
                        'completed'
                )
                    return <span className="text-gray-400">---</span>

                const firstFile = attachments[0]
                return (
                    <div className="flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span
                            className="text-xs text-primary hover:underline cursor-pointer truncate flex-1"
                            title={firstFile?.attachment_file_name}
                            onClick={(e) => {
                                e.stopPropagation()
                                openFilePreview(firstFile)
                            }}
                        >
                            {firstFile?.attachment_file_name}
                        </span>
                        {attachments.length > 1 && (
                            <span className="text-xs text-gray-400 shrink-0">
                                +{attachments.length - 1}
                            </span>
                        )}
                        {attachments.length === 1 && (
                            <div className="flex items-center gap-0.5 shrink-0">
                                <button
                                    className="p-0.5 rounded hover:bg-gray-100 text-primary"
                                    title={t('preview')}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        openFilePreview(firstFile)
                                    }}
                                >
                                    <EyeIcons className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    className="p-0.5 rounded hover:bg-gray-100 text-green-600"
                                    title={t('download')}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        downloadFileWithPresigned(firstFile)
                                    }}
                                >
                                    <DownLoadIcons className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                )
            },
        },
    ])

    return { columns, attachmentExpandable }
}
